// ─────────────────────────────────────────────────────────────────────────
// Checkout-return → confirmation mapping (PANDA-1500 follow-up).
//
// After hosted Stripe Checkout, the user lands back on a Panda page with a
// query flag describing what they just did: ?trial=started, ?topup=true,
// ?upgraded=true, ?subscribed=…, ?card_added=true. Each maps to a DIFFERENT
// post-purchase confirmation modal (or none). This module is the single, pure,
// testable source of truth for that mapping so every return surface — the
// builder page AND the projects page — resolves the SAME modal for the SAME
// purchase, and a $1 trial can never surface the credit top-up modal.
//
// The bug this kills: a trial that returned to /projects (the trial's default
// returnTo, and the signup surface's hardcoded one — see
// app/api/billing/trial/start/route.ts + app/start/start-client.tsx) showed the
// wrong confirmation, because the projects page only knew about ?upgraded.
// Encoding the trial / top-up / plan distinction ONCE — and driving it off the
// SERVER's reconcile result, not just the client query flag — means the
// surfaces can't drift apart again.
// ─────────────────────────────────────────────────────────────────────────

import {
  TRIAL_CREDITS,
  TRIAL_DAYS,
  TRIAL_CONVERT_CENTS,
  TRIAL_CONVERT_CREDITS,
} from "@/lib/trial/config";
import type { PlanId } from "@/lib/plans";
import type { PaymentProviderId } from "@/lib/payments/provider";
import type { PurchaseConfirmationInput } from "@/lib/billing/purchase-confirmation";

// ── 1. What kind of checkout return is this? ──────────────────────────────
export type CheckoutReturnType =
  | "trial"
  | "topup"
  | "upgraded"
  | "subscribed"
  | "card_added";

export interface CheckoutReturn {
  type: CheckoutReturnType | null;
  /**
   * The ref the return page hands to POST /api/billing/reconcile.
   *
   * Stripe puts a checkout session id (`cs_…`) in `session_id`. CHARGEBEE PUTS
   * NEITHER — its hosted page appends its OWN pair, `?id=<hosted_page_id>&
   * state=succeeded`, and the reconcile route's Chargebee arm is keyed on
   * exactly that hosted-page id. Reading only `session_id` is what left every
   * Chargebee hosted return with no ref at all: the reconcile effects bail on
   * `!sessionId`, so the $1 trial had NO return-page backstop and depended
   * entirely on the webhook landing before the buyer looked at the page.
   *
   * The name stays `sessionId` because that is the field every caller already
   * posts; what changed is that it can now be filled from either shape.
   */
  sessionId: string | null;
  /** The `?subscribed=<value>` payload: "plan" | "topup" | "cancelled". */
  subscribedValue: string | null;
}

/**
 * The checkout ref, from whichever param this processor uses.
 *
 * `session_id` WINS whenever it is present, so nothing about a Stripe return
 * changes. The Chargebee fallback is gated on `state=succeeded` — its
 * hosted page sets that pair together and no Stripe or Paddle return carries
 * it — so a stray `?id=` on some unrelated URL can never be mistaken for a
 * checkout ref. An abandoned or in-flight page arrives as `state=cancelled` /
 * `state=not_paid` and is correctly read as "no ref, nothing to reconcile".
 *
 * The caller still says WHICH processor minted it (reconcile dispatches on the
 * posted `provider`, never on the shape of the ref — see lib/payments/refs.ts);
 * this only finds the string.
 *
 * EXPORTED because nine reconcile-on-return effects across six files read the
 * ref for themselves (the builder page, Settings → Billing, Settings → Usage,
 * the rated screens, /projects, the global host). Every one of them read
 * `session_id` directly, which is why a Chargebee return reconciled on none of
 * them. One function so a seventh surface cannot be added with the old shape —
 * the same rule lib/payments/refs.ts sets for column names.
 */
export function returnRef(p: URLSearchParams): string | null {
  const stripe = p.get("session_id");
  // An UNSUBSTITUTED `{CHECKOUT_SESSION_ID}` is not a ref — it is Stripe's
  // token arriving on a return Stripe did not mint, because six mint routes
  // build one successUrl that every provider branch reuses. Treating it as a
  // ref would shadow the real id the other processor appended and post a
  // literal placeholder to reconcile. The Chargebee adapter strips it before
  // the buyer ever sees it (withoutStripePlaceholder); this is the independent
  // second guard for any path that does not go through the adapter.
  if (stripe && !/^\{CHECKOUT_SESSION_ID\}$/i.test(stripe.trim())) return stripe;
  const chargebee = p.get("state") === "succeeded" ? p.get("id") : null;
  if (chargebee) return chargebee;

  // ── PayPal ────────────────────────────────────────────────────────────
  //
  // Added last, so no Stripe or Chargebee return changes shape.
  //
  // PayPal arrives by two different roads and only one of them is ours:
  //
  //   • SUBSCRIPTIONS return straight to the app (they never touch
  //     /api/paypal/return — an approved subscription bills itself), and
  //     PayPal itself appends `subscription_id` to the URL it was given.
  //   • ORDERS go through /api/paypal/return so the capture can be taken,
  //     and that route appends `paypal_order` on its way past.
  //
  // Without this branch `returnRef` answered null on every PayPal return, and
  // because the reconcile-on-return effects are all written `if (sessionId)`,
  // the whole block — reconcile, the "confirming your purchase" state and the
  // success confirmation — was skipped. That is PANDA-2288 (trial credits
  // appearing only when the webhook landed, 30–40s later) and PANDA-2289 (a
  // completed purchase returning to an unchanged page).
  //
  // `subscription_id` is preferred: on a subscription return PayPal also sends
  // a `token`, which is the billing-agreement token and not something any
  // reconcile arm can look up.
  const paypal = p.get("subscription_id") || p.get("paypal_order");
  if (paypal) return paypal;

  // ── Whop ──────────────────────────────────────────────────────────────
  //
  // THE THIRD TIME THIS EXACT BUG SHIPPED. Chargebee's `id`/`state` was
  // missing here, then PayPal's two keys were missing here, and each time a
  // completed purchase came back to a page that found no ref, ran no
  // reconcile and showed no confirmation. Whop was added as the fifth
  // processor and its keys were taught to app/checkout/return ALONE, so every
  // other return surface — the builder above all — went silently blind again
  // (PANDA-2397). RETURN_REF_PARAMS below is what finally makes a sixth
  // processor impossible to add without answering this question.
  //
  // Whop names its own keys and sends no `state`:
  //   • A PAID checkout returns `payment_id` and `receipt_id`, both the pay_…
  //     capture id.
  //   • A $0 trial takes no payment, so Whop creates no capture and the
  //     membership is the only id: `membership_id` = mem_….
  //
  // Presence alone is the signal, and it is safe to read that way: an
  // abandoned or declined Whop checkout never reaches the redirect_url at all,
  // reconcile re-fetches the object from Whop and checks OUR metadata for
  // ownership rather than trusting the URL, and the confirmation itself is
  // gated on the entitlement (isTrialActivated) and not on the ref. A ref
  // invented by hand therefore buys nothing and confirms nothing.
  return p.get("payment_id") || p.get("receipt_id") || p.get("membership_id");
}

/**
 * Every query parameter that can carry a checkout ref, per processor.
 *
 * A TOTAL Record over PaymentProviderId, and that totality is the whole point:
 * adding a sixth processor to that union does not COMPILE until its return
 * keys are declared here. Three separate processors have now shipped with
 * their return key taught to one surface and missing from `returnRef`
 * (Chargebee, PayPal, Whop/PANDA-2397), each costing a silent
 * no-reconcile-no-confirmation on every other surface, so a comment asking the
 * next author to remember was demonstrably not enough.
 *
 * Read by tests/unit/lib/billing/checkout-return-provider-refs.test.ts, which
 * asserts `returnRef` actually resolves every key listed here — the table
 * cannot drift into decoration.
 */
export const RETURN_REF_PARAMS: Record<PaymentProviderId, readonly string[]> = {
  stripe: ["session_id"],
  // Paddle returns through the same successUrl Stripe's mint builds.
  paddle: ["session_id"],
  // Paired with `state=succeeded`; a bare `id` is not a ref.
  chargebee: ["id"],
  paypal: ["subscription_id", "paypal_order"],
  whop: ["payment_id", "receipt_id", "membership_id"],
};

/**
 * Strip every processor's checkout-return parameter from a URL.
 *
 * The surfaces that handle a return all clean the address bar afterwards, and
 * each had its own hand-written delete list — `session_id`, `id`, `state` —
 * written before PayPal and Whop existed. A leftover `payment_id` is not
 * merely untidy: it is a live ref sitting in a URL the user can bookmark,
 * share or reload, and it is the thing a duplicate-confirmation bug is built
 * out of. Driving the cleanup off the same table as the read means a
 * processor's key can never be readable-but-not-clearable.
 */
export function clearReturnRefParams(target: URL | URLSearchParams): void {
  // Both shapes, because the surfaces genuinely hold both: the builder and
  // /projects mutate a URL, while Settings and the rated screens rebuild
  // `url.search` from a detached URLSearchParams. Accepting one and making the
  // other convert is how you get a surface that quietly keeps its own list.
  const params = target instanceof URL ? target.searchParams : target;
  for (const keys of Object.values(RETURN_REF_PARAMS)) {
    for (const key of keys) params.delete(key);
  }
  // Chargebee's paired flag. Not a ref by itself, so it is not in the table,
  // but it is half of one and must not outlive the return either.
  params.delete("state");
}

/**
 * Classify a return URL's query string. Trial is checked FIRST and wins over
 * every other flag: a $1 trial must never be mistaken for a credit top-up, so
 * even a malformed URL carrying both ?trial=started and ?topup=true resolves to
 * "trial". Returns type:null when the query is not a checkout return at all.
 */
export function readCheckoutReturn(search: string): CheckoutReturn {
  const p = new URLSearchParams(search);
  const sessionId = returnRef(p);
  if (p.get("trial") === "started")
    return { type: "trial", sessionId, subscribedValue: null };
  if (p.get("topup") === "true")
    return { type: "topup", sessionId, subscribedValue: null };
  if (p.get("upgraded") === "true")
    return { type: "upgraded", sessionId, subscribedValue: null };
  const subscribed = p.get("subscribed");
  if (subscribed)
    return { type: "subscribed", sessionId, subscribedValue: subscribed };
  if (p.get("card_added") === "true")
    return { type: "card_added", sessionId, subscribedValue: null };
  return { type: null, sessionId: null, subscribedValue: null };
}

/**
 * Routes that run their OWN checkout-return handler (reconcile + confirmation).
 * The global <CheckoutReturnHost/> skips these so a return can never reconcile
 * twice or flash two confirmations; every other route falls back to the host,
 * which is what lets a purchase return to the page it started from instead of
 * being forced to /settings/billing.
 *
 * Prefixes, matched on exact path or path segment — never bare `startsWith`, so
 * "/projects-archive" can't be mistaken for "/projects".
 */
const SELF_HANDLED_RETURN_PATHS = [
  "/projects", // app/projects/page.tsx
  "/project", // app/project/[id]/page.tsx (the builder)
  "/settings/billing", // components/settings/billing/current-plans-card.tsx
  "/settings/usage", // components/settings/credits-usage-card.tsx
  "/checkout", // app/checkout/return — owns reconcile + the hand-off
  // "/pricing" WAS listed here, pointing at components/pricing/pricing-section.tsx.
  // That file has never contained a return handler — no readCheckoutReturn, no
  // returnRef, no /api/billing/reconcile call. So the claim was false in the one
  // direction that loses purchases: <CheckoutReturnHost/> skipped /pricing
  // because the page said it had this covered, and the page did nothing. Every
  // checkout returning to /pricing therefore reconciled nowhere and confirmed
  // nothing, on EVERY provider — a buyer who upgraded from the pricing page was
  // handed back the same "Launch my business" CTA they had just paid through,
  // and only a hard refresh showed the plan (PANDA-2288 / PANDA-2289).
  //
  // Removing the entry hands /pricing to the global host, which is generic and
  // already does both jobs. Re-add it ONLY together with a real handler.
] as const;

/** True when `pathname` already handles its own checkout return. */
export function ownsCheckoutReturn(pathname: string): boolean {
  return SELF_HANDLED_RETURN_PATHS.some(
    (p) => pathname === p || pathname.startsWith(`${p}/`),
  );
}

// ── 2. Server signals we read back after reconcile ────────────────────────
/** Subset of POST /api/billing/reconcile's JSON we depend on. */
export interface ReconcileResult {
  reconciled?: boolean;
  /** What the server actually fulfilled: "trial" | "topup" | "subscription" | "upgrade". */
  kind?: string;
  /** Credits granted by the fulfilled purchase, when the server reports them. */
  credits?: number;
  plan?: string;
  reason?: string;
}

/** Subset of GET /api/credits we read to confirm activation + name balances. */
export interface EntitlementSnapshot {
  plan?: string | null;
  credits_remaining?: number | null;
  monthly_allocation?: number | null;
  // The rest of what /api/credits returns. Optional and unused by the decision
  // itself — carried so a caller that refreshes its own counters off this
  // snapshot does not have to re-fetch or cast it away.
  credits_total?: number | null;
  credits_monthly?: number | null;
  rollover_credits?: number | null;
  pack_credits?: number | null;
  /**
   * Whether the account is STILL inside a live trial.
   *
   * Without this the snapshot could not tell a trialist from a paying Pro
   * subscriber — a trial writes plan='pro', so `plan` reads identically on both
   * sides of a conversion and `isTrialActivated` is true before the upgrade as
   * well as after. That made the upgrade confirmation unfalsifiable: it said
   * "You're on Pro" to someone whose conversion had not provisioned at all
   * (PANDA-2292).
   *
   * Every producer already sends it — this is the `trial` field of
   * /api/credits, which is what all of them fetch — so declaring it here is
   * what makes it visible to decideConfirmation rather than a new read.
   */
  trial?: boolean | null;
}

/**
 * A trial is genuinely active ONLY when the account flipped to Pro. reconcile
 * returning reconciled:true (or already_processed) is necessary but NOT
 * sufficient: a card_reused / prepaid / amount_mismatch rejection also returns
 * without granting and leaves the plan 'free' (see
 * lib/billing/trial-fulfillment.ts). Gating on the fact — plan === 'pro' — is
 * what stops a failed/rejected trial from rendering a false success. The fail
 * direction is deliberate: an unknown/absent plan reads as NOT activated, so we
 * never celebrate a trial that did not start.
 */
export function isTrialActivated(
  fresh: EntitlementSnapshot | null | undefined,
): boolean {
  return fresh?.plan === "pro";
}

/**
 * Reconcile reasons that mean "not finished yet", not "it failed".
 *
 * `payment_pending` — the invoice/intent is still settling.
 * `schedule_pending` — fulfilment hit a transient Stripe error (very often the
 *   object lock raised while a CONCURRENT fulfilment writes the same trial
 *   schedule) and deliberately left the paid subscription alone for a retry, in
 *   place of the cancel + refund that used to destroy live trials.
 *
 * Both are worth re-asking for, and neither may be shown to the buyer as a
 * failed, refunded trial.
 */
const RETRYABLE_RECONCILE_REASONS = new Set(["payment_pending", "schedule_pending"]);

export function isRetryableReconcileReason(reason?: string | null): boolean {
  return RETRYABLE_RECONCILE_REASONS.has(reason ?? "");
}

/**
 * Rejections that are FINAL: the server made a deliberate decision, cancelled
 * the subscription and refunded the $1. Only these may short-circuit a trial
 * return straight to the failure screen — everything else (a slow webhook, a
 * retryable reason, no reconcile result at all) has to be re-checked against
 * the entitlement first, because the trial may still be landing.
 */
const TERMINAL_TRIAL_REJECTIONS = new Set([
  "card_reused",
  "prepaid_card",
  "amount_mismatch",
]);

export function isTerminalTrialRejection(reason?: string | null): boolean {
  return TERMINAL_TRIAL_REJECTIONS.has(reason ?? "");
}

// ── 3. Confirmation input builders (single source per kind) ───────────────
/**
 * The trial confirmation input. Credits come from the server's reconcile grant
 * when present, else the config default; the length + renewal terms ALWAYS come
 * from lib/trial/config.ts, so the modal can never contradict the real offer.
 */
export function trialConfirmationInput(args: {
  reconcileCredits?: number | null;
  source: string;
  projectId?: string | null;
  ctaLabel?: string;
}): Extract<PurchaseConfirmationInput, { kind: "trial" }> {
  return {
    kind: "trial",
    credits:
      typeof args.reconcileCredits === "number"
        ? args.reconcileCredits
        : TRIAL_CREDITS,
    days: TRIAL_DAYS,
    renewsPriceUsd: Math.round(TRIAL_CONVERT_CENTS / 100),
    renewsCredits: TRIAL_CONVERT_CREDITS,
    source: args.source,
    projectId: args.projectId ?? null,
    cta: { label: args.ctaLabel ?? "Start building" },
  };
}

/** The credit top-up confirmation input. */
export function topupConfirmationInput(args: {
  balanceAfter?: number | null;
  creditsAdded?: number | null;
  source: string;
  projectId?: string | null;
  ctaLabel?: string;
}): Extract<PurchaseConfirmationInput, { kind: "credits" }> {
  return {
    kind: "credits",
    creditsAdded: args.creditsAdded ?? null,
    balanceAfter:
      typeof args.balanceAfter === "number" ? args.balanceAfter : null,
    source: args.source,
    projectId: args.projectId ?? null,
    cta: { label: args.ctaLabel ?? "Continue building" },
  };
}

/** The plan (new subscription / upgrade) confirmation input. */
export function planConfirmationInput(args: {
  planId: PlanId;
  /** Plan BEFORE the purchase, when the caller knows it. Passing the SAME plan
   *  is what selects the "More monthly credits unlocked" copy over the neutral
   *  "You're on Pro". Omit (undefined) to keep the neutral copy. */
  previousPlanId?: PlanId | null;
  monthlyCredits?: number | null;
  source: string;
  projectId?: string | null;
  ctaLabel?: string;
  /** Where the CTA navigates. null (the default) dismisses in place, which is
   *  what a return to the page the purchase started from wants. */
  ctaHref?: string | null;
}): Extract<PurchaseConfirmationInput, { kind: "plan" }> {
  return {
    kind: "plan",
    planId: args.planId,
    ...(args.previousPlanId !== undefined
      ? { previousPlanId: args.previousPlanId }
      : {}),
    monthlyCredits: args.monthlyCredits ?? null,
    source: args.source,
    projectId: args.projectId ?? null,
    cta: {
      label: args.ctaLabel ?? "Start building",
      // Omitted rather than null-ed so the input keeps the same shape it always
      // had when no href is asked for; the resolver defaults it to null anyway.
      ...(args.ctaHref !== undefined ? { href: args.ctaHref } : {}),
    },
  };
}

/**
 * The $1-trial → Pro EARLY-CONVERT confirmation. When a trial user burns their
 * 100 credits and upgrades to paid Pro NOW (app/api/billing/trial/convert), the
 * flow is IN-APP (no Stripe redirect), so nothing in the checkout-return path
 * fires — the modal used to just close, leaving the user unsure the upgrade even
 * happened (the reported gap). This surfaces the SAME post-upgrade confirmation a
 * normal Pro upgrade shows: previousPlanId:'free' selects the celebratory
 * "Welcome to Pro / your subscription is active" copy, because the trialist has
 * just become a first-time PAYING subscriber. The Pro allowance + price come from
 * lib/trial/config so the modal can't contradict what conversion actually grants.
 */
export function trialConvertConfirmationInput(args: {
  source: string;
  projectId?: string | null;
  ctaLabel?: string;
}): Extract<PurchaseConfirmationInput, { kind: "plan" }> {
  return {
    kind: "plan",
    planId: "pro",
    previousPlanId: "free",
    monthlyCredits: TRIAL_CONVERT_CREDITS,
    monthlyPriceUsd: Math.round(TRIAL_CONVERT_CENTS / 100),
    cadence: "monthly",
    source: args.source,
    projectId: args.projectId ?? null,
    cta: { label: args.ctaLabel ?? "Start building" },
  };
}

// ── 4. Authoritative decision ─────────────────────────────────────────────
export type ConfirmationDecision =
  | { status: "confirm"; input: PurchaseConfirmationInput }
  /** A trial return the server DELIBERATELY rejected — cancelled and refunded. */
  | { status: "trial_failed"; reason?: string }
  /**
   * A trial that has not landed YET. Not a failure: the money may well have
   * been taken and the subscription may already be live — fulfilment simply
   * has not finished inside the return page's patience budget.
   */
  | { status: "trial_pending"; reason?: string }
  /**
   * An upgrade return whose account can still be SEEN to be on the trial it was
   * meant to leave. Never a success: the payment may well have gone through,
   * but the entitlement has not, so the honest thing is a processing state the
   * user can act on rather than a confirmation that contradicts their account.
   */
  | { status: "upgrade_pending"; reason?: string }
  /** Nothing to confirm (unknown/absent kind, or a cancelled checkout). */
  | { status: "none" };

/**
 * Decide which confirmation to show from the SERVER's reconcile result first,
 * the client return flag second. Driving off reconcile.kind (what was actually
 * fulfilled) is what guarantees the three purchase types never cross: a trial
 * renders the trial modal, a top-up the top-up modal, a plan the plan modal —
 * regardless of which query flag brought the user back.
 *
 * Precedence is trial → top-up → plan, so the safe direction always wins: if a
 * return somehow looks like both a trial and a top-up, it is treated as the
 * trial (never the top-up the bug wrongly showed).
 */
export function decideConfirmation(args: {
  returnType: CheckoutReturnType;
  reconcile: ReconcileResult | null;
  fresh: EntitlementSnapshot | null;
  source: string;
  projectId?: string | null;
}): ConfirmationDecision {
  const { returnType, reconcile, fresh, source, projectId } = args;
  const serverKind = reconcile?.kind;

  if (returnType === "trial" || serverKind === "trial") {
    if (isTrialActivated(fresh)) {
      return {
        status: "confirm",
        input: trialConfirmationInput({
          reconcileCredits: reconcile?.credits,
          source,
          projectId,
        }),
      };
    }
    // "Not activated YET" is not "activation failed", and only one of those may
    // promise a refund. TERMINAL_TRIAL_REJECTIONS already says so in its own
    // doc — the server cancels and refunds before setting one of those reasons.
    // Everything else reaching here (a slow webhook, a 3DS step, a retryable
    // reason, no reconcile result at all) is a return page that ran out of
    // patience, and a live PayPal trial has been shown as a failed, refunded
    // one on exactly that path.
    if (isTerminalTrialRejection(reconcile?.reason)) {
      return { status: "trial_failed", reason: reconcile?.reason };
    }
    return { status: "trial_pending", reason: reconcile?.reason };
  }

  if (returnType === "topup" || serverKind === "topup") {
    return {
      status: "confirm",
      input: topupConfirmationInput({
        balanceAfter: fresh?.credits_remaining,
        source,
        projectId,
      }),
    };
  }

  if (
    returnType === "upgraded" ||
    serverKind === "subscription" ||
    serverKind === "upgrade"
  ) {
    // ── Only confirm an upgrade we can believe ────────────────────────
    //
    // The trial branch above has always gated on the resulting entitlement
    // (`isTrialActivated`). This branch gated on nothing — `returnType` alone —
    // so a return to ?upgraded=true confirmed "You're on Pro" whether or not
    // anything had been provisioned. For a trial→Pro conversion that is not
    // merely optimistic, it is unfalsifiable: a trialist ALREADY reads
    // plan='pro', so the confirmation could never have been wrong-looking.
    //
    // Deliberately narrow. We withhold only when the account can be POSITIVELY
    // seen to still be trialing; an absent or failed `fresh` read is not
    // evidence of failure and must not suppress a real confirmation, and a
    // server that reconciled the purchase is authority enough on its own.
    const serverVerified = reconcile?.reconciled === true;
    if (!serverVerified && fresh?.trial === true) {
      return { status: "upgrade_pending", reason: reconcile?.reason };
    }
    const planId = (fresh?.plan ?? "pro") as PlanId;
    // A rated "Add more credits" raise reconciles as kind:"upgrade" — the user
    // stays on the SAME plan and only the monthly allocation goes up. Saying so
    // explicitly is what makes the modal read "More monthly credits unlocked"
    // instead of the neutral "You're on Pro / your plan is active", and the
    // server kind is the ONLY signal a redirect return has: the pre-purchase
    // plan is lost across the round-trip to Stripe. kind:"subscription" (a new
    // or changed plan) deliberately leaves previousPlanId unset so the resolver
    // keeps its neutral — and still correct — copy.
    const isCreditRaise = serverKind === "upgrade";
    return {
      status: "confirm",
      input: planConfirmationInput({
        planId,
        previousPlanId: isCreditRaise ? planId : undefined,
        monthlyCredits: fresh?.monthly_allocation ?? null,
        source,
        projectId,
        // They raised their allowance mid-build — the CTA continues that work
        // rather than inviting them to start over.
        ctaLabel: isCreditRaise ? "Continue building" : undefined,
      }),
    };
  }

  return { status: "none" };
}

/**
 * User-facing copy for a trial that did NOT activate, keyed by the reconcile
 * reason. Shared so every surface explains "paid but got nothing" the same,
 * honest way (and mentions the refund) instead of a false success.
 */
/**
 * Copy for a trial that has not landed yet. Neither a success nor a failure:
 * it must not congratulate anyone, and it must not promise a refund that is
 * not coming — the subscription is very likely alive.
 */
export function trialPendingMessage(): string {
  return "Your payment went through — we're finishing your trial setup now. This updates on its own, no need to refresh.";
}

/**
 * Copy for a pending purchase the app watched and STILL could not see land.
 *
 * Separate from the message above because it makes the opposite promise: the
 * first says "we are watching this for you", and printing that a second time
 * after the watch has given up would be the instruction-to-refresh problem in
 * a politer costume. Once we have stopped looking, the honest next step is a
 * human — so this is the only pending copy that asks the user to do anything.
 */
export function trialUnresolvedMessage(): string {
  return "Your payment went through, but your trial is taking longer than usual to activate. Contact us and we'll get it sorted.";
}

/**
 * The plan-upgrade equivalents of the two above. They lived as inline literals
 * in /projects and the global return host — the same sentence written twice,
 * which is how one of them would eventually be fixed and the other not.
 */
export function upgradePendingMessage(): string {
  return "Your payment went through — we're activating your plan now. This updates on its own, no need to refresh.";
}

export function upgradeUnresolvedMessage(): string {
  return "Your payment went through, but your plan is taking longer than usual to activate. Contact us and we'll get it sorted.";
}

export function trialFailureMessage(reason?: string): string {
  switch (reason) {
    case "card_reused":
      return "This card has already been used for a trial. If you were charged, it's been refunded.";
    case "amount_mismatch":
      return "We couldn't verify the trial charge, so the trial wasn't started.";
    case "prepaid_card":
      return "Prepaid cards can't be used for the trial. If you were charged, it's been refunded.";
    // NOT a refund message. The subscription is alive and the $1 was kept on
    // purpose — fulfilment is simply still finishing, and telling this buyer
    // they will be refunded would be a lie that also invites a support ticket.
    case "schedule_pending":
      return trialPendingMessage();
    default:
      return "We couldn't start your trial. If you were charged, it will be refunded.";
  }
}

// ─────────────────────────────────────────────────────────────────────────
// WAITING FOR THE PURCHASE TO LAND — one implementation, two surfaces.
//
// A checkout return can arrive before the webhook that fulfils it. PayPal's
// fulfilment was measured at ~10s in production, and until it lands the account
// still reads exactly as it did before the purchase: plan 'free', no trial.
// Deciding at that moment produces the worst possible answer — telling a buyer
// whose money HAS moved that their trial failed.
//
// The checkout return page already waited. The project page did not: it ran one
// reconcile, read the entitlement once, and called toast.error the moment the
// plan was not yet 'pro'. Two surfaces, one race, one of them losing it every
// time — which is exactly the drift that comes from each caller owning its own
// timing. So the waiting lives here now and both call it.
//
// I/O is injected rather than imported. This module is pure and unit-tested,
// and the two callers fetch differently (one has projectId in scope, one does
// not); passing the fetchers in keeps that difference at the edge and lets the
// tests drive the clock without waiting real seconds.
// ─────────────────────────────────────────────────────────────────────────

/** Reconcile retries. Short: reconcile is cheap and usually answers first try. */
export const RETURN_RECONCILE_ATTEMPTS = 4;
export const RETURN_RECONCILE_DELAY_MS = 1500;
/**
 * Entitlement retries — the real patience budget, ~24s.
 *
 * Sized off a measured production fulfilment: a PayPal trial where the capture
 * landed at 14:55:32.8 and the entitlement appeared at 14:55:42.9. A budget
 * under that turns an ordinary slow webhook into a false failure.
 */
export const RETURN_ENTITLEMENT_ATTEMPTS = 16;
export const RETURN_ENTITLEMENT_DELAY_MS = 1500;

const wait = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

export interface SettleReturnIO {
  /** POST /api/billing/reconcile. Returns null on a network failure. */
  reconcile: () => Promise<ReconcileResult | null>;
  /** GET /api/credits with no-store. Returns null when unreadable. */
  readEntitlement: () => Promise<EntitlementSnapshot | null>;
  /** Trials get the patience budget; a top-up has nothing to wait for. */
  isTrial: boolean;
  /** Injectable so tests do not sleep for real. */
  sleep?: (ms: number) => Promise<void>;
}

/**
 * Reconcile, then wait for the entitlement, then hand both to
 * decideConfirmation. Never throws: every failure degrades to a null the
 * decision already fails closed on.
 */
export async function settleCheckoutReturn(io: SettleReturnIO): Promise<{
  reconcile: ReconcileResult | null;
  fresh: EntitlementSnapshot | null;
}> {
  const sleep = io.sleep ?? wait;

  let reconcile: ReconcileResult | null = null;
  for (let attempt = 0; attempt < RETURN_RECONCILE_ATTEMPTS; attempt++) {
    try {
      const data = await io.reconcile();
      reconcile = data;
      // Only the not-finished-yet reasons are worth re-asking for. A terminal
      // answer — granted, already_processed, or a deliberate rejection — will
      // not change by asking again.
      if (!isRetryableReconcileReason(data?.reason)) break;
    } catch {
      /* network hiccup — fall through to the retry */
    }
    if (attempt < RETURN_RECONCILE_ATTEMPTS - 1) {
      await sleep(RETURN_RECONCILE_DELAY_MS);
    }
  }

  let fresh = await io.readEntitlement().catch(() => null);

  // Give a trial that has not shown up yet the benefit of the doubt — unless
  // the server made a deliberate, final rejection, which has already cancelled
  // and refunded. There is nothing left to wait for in that case.
  if (
    io.isTrial &&
    !isTrialActivated(fresh) &&
    !isTerminalTrialRejection(reconcile?.reason)
  ) {
    for (
      let i = 0;
      i < RETURN_ENTITLEMENT_ATTEMPTS && !isTrialActivated(fresh);
      i++
    ) {
      await sleep(RETURN_ENTITLEMENT_DELAY_MS);
      fresh = await io.readEntitlement().catch(() => null);
    }
  }

  return { reconcile, fresh };
}
